import { CobaltIconCheckmarkCircleSolid } from '@cae/cobalt-react/icons/solid/CobaltIconCheckmarkCircleSolid';
import { formatAmount } from '@features/invoices/utils/invoices.utils';
import { DataValue } from '@shared/components/data-value/DataValue';
import { FEATURE_FLAGS } from '@shared/constants/featureFlags.constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';

import type { OutstandingAmountCellProps } from './OutstandingAmountCell.types';

import styles from './OutstandingAmountCell.module.css';
import {
  getOutstandingAmountDisplay,
  shouldAmountBePastDueColor,
  OUTSTANDING_AMOUNT_DISPLAY_TYPE,
} from './OutstandingAmountCell.utils';

const OutstandingAmountCell = React.memo(
  ({
    amount,
    currency,
    balanceStatus,
    isPastDue,
  }: Readonly<OutstandingAmountCellProps>): React.ReactElement => {
    const {
      [FEATURE_FLAGS.manageInvoicesCreditMemosEnabled]: manageInvoicesCreditMemosEnabled,
    } = useFlags();

    if (amount == null) {
      return <DataValue value={null} />;
    }

    const displayType = getOutstandingAmountDisplay(balanceStatus);
    const shouldBePastDueColor = shouldAmountBePastDueColor(isPastDue, amount);

    if (
      displayType === OUTSTANDING_AMOUNT_DISPLAY_TYPE.PAID ||
      (!manageInvoicesCreditMemosEnabled &&
        displayType === OUTSTANDING_AMOUNT_DISPLAY_TYPE.FULL_CREDIT)
    ) {
      return (
        <span
          className={styles.statusCell}
          data-testid="outstanding-amount-cell"
        >
          <span className={`${styles.paidText} body-02`}>Paid</span>
          <CobaltIconCheckmarkCircleSolid
            className={styles.iconPaid}
            aria-hidden="true"
          />
        </span>
      );
    }

    if (
      manageInvoicesCreditMemosEnabled &&
      displayType === OUTSTANDING_AMOUNT_DISPLAY_TYPE.FULL_CREDIT
    ) {
      return (
        <span
          className={styles.statusCell}
          data-testid="outstanding-amount-cell"
        >
          <span className={`${styles.fullCreditText} body-02`}>
            Fully credited
          </span>
        </span>
      );
    }

    // displayType === OUTSTANDING_AMOUNT_DISPLAY_TYPE.AMOUNT
    return (
      <span
        className={shouldBePastDueColor ? styles.amountPastDue : undefined}
        data-testid="outstanding-amount-cell"
      >
        {formatAmount(amount, currency)}
      </span>
    );
  }
);
OutstandingAmountCell.displayName = 'OutstandingAmountCell';

export { OutstandingAmountCell };
